import { Divider } from '@/components/Divider'
import * as Icon from '@/materials/icons/all'
import * as M from '@/materials'
import { ShareButtons } from '@ta-interaktiv/react-share-buttons'
import * as React from 'react'
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl'
import { css, keyframes } from 'styled-components/macro'
import { useImmer } from 'use-immer'

/**
 * The underlying DOM element which is rendered by this component.
 */
const Root = 'div'

interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  /*
   * TODO: Rename to 'overline'
   */
  header?: React.ReactNode

  text: React.ReactNode
  lead?: React.ReactNode

  /**
   * The timestamp at the bottom of the meta section.
   */
  updatedAt?: 'continuous' | Date
}

const sharingArticleId: Record<string, undefined | string> = {
  de: '553456246692',
  fr: '16112831',
}

function ContentHead(props: Props) {
  const intl = useIntl()

  const { header, text, lead, updatedAt, ...rest } = props

  const [state, mutate] = useImmer({
    now: undefined as undefined | Date,
  })

  React.useEffect(() => {
    mutate((draft) => {
      draft.now = new Date()
    })
  }, [mutate])

  return (
    <Root {...rest}>
      <h2 css={styles.title as any}>
        {header && <div css={styles.header as any}>{header}</div>}
        <div>{text}</div>
      </h2>
      <div css={styles.lead as any}>{lead}</div>

      <Divider css={styles.divider as any} />

      <div css={styles.meta as any}>
        <div css={styles.actions as any}>
          <div css={styles.share as any}>
            <ShareButtons articleId={sharingArticleId[intl.locale] ?? ''} hashtags={['CHvote']} />
          </div>
        </div>

        <div css={styles.authors as any}>
          <FormattedMessage
            id="fDTeR8b"
            defaultMessage="Marc Brupbacher, Titus Plattner (Projektleitung und Konzept); Interactive Things, Kaspar Manz (Programmierung); Matthias Saner/Sanerstudio (Gestaltung); Mathieu Rudaz (Übersetzung)"
          />
        </div>

        {updatedAt &&
          (() => {
            if (updatedAt === 'continuous') {
              return (
                <div css={styles.timestamp as any}>
                  <span className="icon">
                    <Icon.Refresh16 />
                  </span>
                  <FormattedMessage id="mrMUFay" defaultMessage="Wird laufend aktualisiert" />
                </div>
              )
            }

            const { now } = state
            if (!now) {
              return (
                <time css={styles.timestamp as any} dateTime={updatedAt.toISOString()}>
                  {updatedAt.toISOString()}
                </time>
              )
            }

            const timeAgo = (updatedAt.getTime() - now.getTime()) / 1000

            if (Math.abs(timeAgo) < 60 * 60) {
              return (
                <time css={styles.timestamp as any} dateTime={updatedAt.toISOString()}>
                  <span className="icon">
                    <Icon.Refresh16 />
                  </span>
                  <FormattedMessage
                    id="zitfvPi"
                    defaultMessage="Aktualisiert {relativeTime}"
                    values={{
                      relativeTime: (
                        <FormattedRelativeTime value={Math.min(-1, Math.round(timeAgo))} updateIntervalInSeconds={1} />
                      ),
                    }}
                  />
                </time>
              )
            }

            return (
              <time css={styles.timestamp as any} dateTime={updatedAt.toISOString()}>
                <FormattedMessage
                  id="dbbnexg"
                  defaultMessage="Zuletzt aktualisiert am {updatedAt, date, ::yyyyMMdd}, {updatedAt, date, ::HHmm}"
                  values={{ updatedAt }}
                />
              </time>
            )
          })()}
      </div>
    </Root>
  )
}

const animations = {
  rotate: keyframes`
    0% {
      transform: rotate(1turn);
    }
    25% {
      transform: rotate(0deg);
    }
  `,
}

const styles = {
  title: css`
    ${M.fontHeading1_};
    margin: 0 0 15px;
  `,
  header: css`
    font-family: var(--font-sans-serif);
    font-size: 15px;
    line-height: 1.3;

    margin-bottom: 5px;

    color: var(--color-primary);
  `,
  lead: css`
    ${M.fontLead_};
  `,

  divider: css`
    margin: 40px 0 20px;
  `,

  meta: css`
    display: grid;

    grid-template-areas: 'authors actions' 'timestamp actions';
    grid-template-columns: 1fr min-content;

    font-family: var(--font-sans-serif);
    font-size: 12px;
    line-height: 1.5;
  `,

  actions: css`
    grid-area: actions;
    margin-left: 20px;
  `,
  share: css`
    position: relative;
    width: 38px;
    height: 38px;
    font-size: 20px;

    & > * {
      position: absolute;
      right: -9px;
      top: -9px;
      background: var(--site-background);
      padding: 9px;
    }
  `,

  authors: css`
    grid-area: authors;
  `,

  timestamp: css`
    grid-area: timestamp;

    color: var(--gray-color);
    margin-top: 4px;

    & > .icon {
      display: inline-block;
      margin: -3px 4px -3px -1px;
    }
    & > .icon > svg {
      animation: ${animations.rotate} 4s 1s infinite;
    }
  `,
}

export default ContentHead
